import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const schoolTypes = [
  { label: '综合类', pct: 45, color: 'bg-primary-500' },
  { label: '理工类', pct: 32, color: 'bg-primary-400' },
  { label: '师范类', pct: 15, color: 'bg-accent-500' },
  { label: '其他', pct: 8, color: 'bg-secondary-400' },
];

const trends = [
  { label: '中国式现代化', direction: 'up' as const },
  { label: '党的二十大精神', direction: 'up' as const },
  { label: '科技创新与自立自强', direction: 'up' as const },
  { label: '共同富裕', direction: 'up' as const },
  { label: '全过程人民民主', direction: 'stable' as const },
];

const dimensions = [
  { label: '价值引领', score: 91 },
  { label: '教学内容', score: 87 },
  { label: '师生互动', score: 82 },
  { label: '课堂管理', score: 79 },
  { label: '案例运用', score: 85 },
];

export default function P73ProvinceMap({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [activeTab, setActiveTab] = useState<'course' | 'common'>('course');

  return (
    <div className="relative min-h-screen flex flex-col py-10 md:py-14">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-accent-500 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一省一图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            看区域趋势与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">资源配置方向</span>
          </h2>
          <p className={`text-sm text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            区域跟踪看趋势和支持方向，不把不同学校、不同课程压缩为单一排名
          </p>
        </div>

        {/* Tab Switcher */}
        <div className={`flex items-center justify-center mb-6 md:mb-8 transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex p-1 rounded-full bg-background-100 border border-background-200/60">
            <button
              onClick={() => setActiveTab('course')}
              className={`px-5 py-2 rounded-full text-xs font-body font-semibold whitespace-nowrap transition-all duration-300 ${
                activeTab === 'course'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-950 hover:text-foreground-950'
              }`}
            >
              按课程维度看
            </button>
            <button
              onClick={() => setActiveTab('common')}
              className={`px-5 py-2 rounded-full text-xs font-body font-semibold whitespace-nowrap transition-all duration-300 ${
                activeTab === 'common'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-950 hover:text-foreground-950'
              }`}
            >
              跨课程共同维度
            </button>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-4 max-w-5xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 高校覆盖 - stat card */}
          <div className="md:col-span-3 p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-2xl text-center flex flex-col justify-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
              <i className="ri-building-3-line text-lg text-primary-600" />
            </div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary-700 mb-1">287</div>
            <p className="text-xs text-foreground-950 font-body mb-2">所高校参与</p>
            <p className="text-sm font-body font-bold text-foreground-950">高校覆盖</p>
          </div>

          {/* 课程覆盖 - stat card */}
          <div className="md:col-span-3 p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-2xl text-center flex flex-col justify-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-accent-100/60 border border-accent-200/40 flex items-center justify-center">
              <i className="ri-file-list-3-line text-lg text-accent-600" />
            </div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-accent-700 mb-1">156</div>
            <p className="text-xs text-foreground-950 font-body mb-2">门课程纳入评价</p>
            <p className="text-sm font-body font-bold text-foreground-950">课程覆盖</p>
          </div>

          {/* 学校类型对比 - wide card with bars */}
          <div className="md:col-span-6 p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
                <i className="ri-bar-chart-line text-sm text-primary-600" />
              </div>
              <h4 className="text-sm font-body font-bold text-foreground-950">学校类型对比</h4>
            </div>
            <div className="space-y-3">
              {schoolTypes.map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-foreground-950 font-body">{s.label}</span>
                    <span className="text-xs font-body font-semibold text-foreground-950">{s.pct}%</span>
                  </div>
                  <div className="h-2 bg-background-200/70 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${s.color}`} style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 课程主题趋势 - left half */}
          <div className="md:col-span-6 p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent-100/60 border border-accent-200/40 flex items-center justify-center">
                <i className="ri-timer-line text-sm text-accent-600" />
              </div>
              <h4 className="text-sm font-body font-bold text-foreground-950">课程主题趋势</h4>
            </div>
            <div className="space-y-2.5">
              {trends.map((t) => (
                <div key={t.label} className="flex items-center justify-between py-2 px-3 rounded-lg bg-background-100/50 border border-background-200/40">
                  <span className="text-xs text-foreground-950 font-body">{t.label}</span>
                  {t.direction === 'up' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-body font-semibold text-primary-700">
                      <i className="ri-arrow-up-line text-xs" />热度上升
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-body font-semibold text-foreground-950">
                      <i className="ri-subtract-line text-xs" />保持稳定
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 质量维度分布 - right half */}
          <div className="md:col-span-6 p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
                <i className="ri-radar-line text-sm text-primary-600" />
              </div>
              <h4 className="text-sm font-body font-bold text-foreground-950">质量维度分布</h4>
            </div>
            <div className="space-y-3">
              {dimensions.map((d) => (
                <div key={d.label} className="flex items-center gap-3">
                  <span className="text-xs text-foreground-950 font-body w-20 text-right whitespace-nowrap">{d.label}</span>
                  <div className="flex-1 h-2.5 bg-background-200/70 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500"
                      style={{ width: `${d.score}%` }}
                    />
                  </div>
                  <span className="text-xs font-body font-bold text-foreground-950 w-7">{d.score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom summary */}
        <div className={`max-w-3xl mx-auto text-center mt-6 md:mt-8 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 bg-background-50/40 backdrop-blur-md border border-background-200/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] rounded-xl">
            <p className="text-sm text-foreground-950 font-body leading-relaxed">
              省域视图的价值，不是给学校做简单排名，而是识别哪些地区课程质量较为稳定、哪些学校已形成可推广经验、哪些课程主题需要<strong className="text-accent-700">加强资源建设</strong>、哪些教师群体需要<strong className="text-accent-700">专题培训</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}